import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { posToDOMRect } from '@tiptap/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { t } from '../../../../../infra/i18n';
import { AiButton } from '../../../../../../app/engagement/components/AiButton';
import { IconButton } from '../../../../../infra/dls/components/icon-button/icon-button';
import { useBreakpoints } from '../../../../../infra/dls/hooks/use-get-breakpoints';
import { ActionBar } from '../../action-bar/action-bar';
import AlignCenterIcon from '../../icons/action-align-center.svg';
import AlignLeftIcon from '../../icons/action-align-left.svg';
import BoldIcon from '../../icons/action-bold.svg';
import ItalicIcon from '../../icons/action-italic.svg';
import LinkIcon from '../../icons/action-link.svg';
import UnderlineIcon from '../../icons/action-underline.svg';
import styles from '../styles.module.scss';
import { LinkPopup } from './link-popup';
var getLinkNodeRangeOnClick = function (editor) {
    var state = editor.state;
    // selection start === caret position in this case because the function is not called on non-empty selections
    var caretPosition = state.selection.from;
    var foundNodeRange = null;
    var parentNode = editor.state.doc.resolve(caretPosition).node();
    parentNode.descendants(function (node, pos) {
        var nodeFrom = pos + 1;
        var nodeTo = nodeFrom + node.nodeSize;
        if (caretPosition >= nodeFrom && caretPosition <= nodeTo) {
            foundNodeRange = { from: nodeFrom, to: nodeTo };
            return false;
        }
        return true;
    });
    return foundNodeRange;
};
var createVirtualSelectionElement = function (editor, isTabletOrSmaller) {
    var state = editor.state, view = editor.view;
    var selection = state.selection;
    var from = selection.from, to = selection.to;
    var selectionRect = posToDOMRect(view, from, to);
    var SPACING = isTabletOrSmaller ? 25 : 0;
    return {
        getBoundingClientRect: function () { return ({
            top: selectionRect.top - SPACING,
            left: selectionRect.left,
            right: selectionRect.right,
            bottom: selectionRect.top,
            width: selectionRect.width,
            height: SPACING,
            x: selectionRect.left - SPACING,
            y: selectionRect.top,
        }); },
    };
};
export var BubbleMenu = function (_a) {
    var _b, _c;
    var editor = _a.editor, onFocus = _a.onFocus, onBlur = _a.onBlur, focused = _a.focused, onOpenAiPopover = _a.onOpenAiPopover, genAiEnabled = _a.genAiEnabled;
    var isTabletOrSmaller = useBreakpoints().isTabletOrSmaller;
    var isActionBarVisible = !editor.state.selection.empty && focused;
    var _d = useState(false), linkPopupOpen = _d[0], setLinkPopupOpen = _d[1];
    var _e = useState(null), virtualElement = _e[0], setVirtualElement = _e[1];
    var linkSelectionRef = useRef(null);
    var ensureProtocol = function (link) {
        return /^(https?:\/\/)/i.test(link.trim()) ? link.trim() : "https://".concat(link.trim());
    };
    var openLinkPopup = function () {
        var _a = editor.state.selection, from = _a.from, to = _a.to;
        var text = editor.state.doc.textBetween(from, to);
        editor.commands.blur();
        var url = null;
        editor.state.doc.nodesBetween(from, to, function (node) {
            if (node.marks) {
                var linkMark = node.marks.find(function (mark) { return mark.type.name === 'link'; });
                if (linkMark) {
                    url = linkMark.attrs.href;
                    return false;
                }
            }
            return true;
        });
        linkSelectionRef.current = { from: from, to: to, text: text, url: url };
        setLinkPopupOpen(true);
    };
    var applyLink = function (state) {
        var text = state.text, url = state.url;
        var formattedUrl = ensureProtocol(url);
        var _a = linkSelectionRef.current, from = _a.from, to = _a.to;
        editor
            .chain()
            .setTextSelection({ from: from, to: to })
            .insertContent(text)
            // when link text is changed, we need to set the selection to the end of the new link text
            .setTextSelection({ from: from, to: from + text.length })
            .setLink({ href: formattedUrl })
            .setBold()
            .setUnderline()
            .run();
        setLinkPopupOpen(false);
    };
    var removeLink = function () {
        var _a = linkSelectionRef.current, from = _a.from, to = _a.to;
        editor.chain().setTextSelection({ from: from, to: to }).unsetLink().unsetBold().unsetUnderline().run();
        setLinkPopupOpen(false);
    };
    var handleSelectionUpdate = function () {
        var isSimpleClick = editor.state.selection.empty;
        var caretPosition = editor.state.selection.from;
        var activeNode = editor.state.doc.nodeAt(caretPosition);
        var isLinkNode = activeNode === null || activeNode === void 0 ? void 0 : activeNode.marks.some(function (mark) { return mark.type.name === 'link'; });
        if (isSimpleClick && isLinkNode) {
            var linkRange = getLinkNodeRangeOnClick(editor);
            if (linkRange) {
                var from = linkRange.from, to = linkRange.to;
                editor.commands.setTextSelection({ from: from, to: to });
                openLinkPopup();
            }
        }
    };
    useEffect(function () {
        editor.on('selectionUpdate', handleSelectionUpdate);
        return function () {
            editor.off('selectionUpdate', handleSelectionUpdate);
        };
    }, [editor]);
    // do not allow to apply link if more than one text node is selected
    var canApplyLink = useMemo(function () {
        var _a;
        var isSingleTextNodeSelected = true;
        var content = (_a = editor.state.selection) === null || _a === void 0 ? void 0 : _a.content();
        while (content) {
            content = content.content;
            if ((content === null || content === void 0 ? void 0 : content.length) > 1) {
                isSingleTextNodeSelected = false;
                break;
            }
        }
        return isSingleTextNodeSelected;
    }, [editor.state.selection]);
    useEffect(function () {
        var updateVirtualElement = function () {
            if (isActionBarVisible && !editor.state.selection.empty) {
                var newVirtualElement = createVirtualSelectionElement(editor, isTabletOrSmaller);
                setVirtualElement(newVirtualElement);
            }
            else {
                setVirtualElement(null);
            }
        };
        updateVirtualElement();
        var handleScroll = function () {
            if (isActionBarVisible) {
                updateVirtualElement();
            }
        };
        window.addEventListener('scroll', handleScroll, true);
        return function () { return window.removeEventListener('scroll', handleScroll, true); };
    }, [editor, isActionBarVisible, isTabletOrSmaller]);
    return (_jsxs(_Fragment, { children: [_jsx(ActionBar, { getMountNode: function () { return virtualElement; }, visible: isActionBarVisible && !!virtualElement, onFocus: onFocus, onBlur: onBlur, compensateScroll: isTabletOrSmaller, children: _jsxs(ActionBar.Row, { children: [genAiEnabled && (_jsx(ActionBar.RowSection, { children: _jsx(AiButton, { text: t('engage:rich_text_editor.refine_text'), onClick: function () { return onOpenAiPopover === null || onOpenAiPopover === void 0 ? void 0 : onOpenAiPopover(); }, onMouseDown: function (e) { return e.preventDefault(); } }) })), _jsxs(ActionBar.RowSection, { children: [_jsx(IconButton.L, { className: editor.isActive('bold') ? styles['active-action'] : '', Icon: _jsx(BoldIcon, {}), onMouseDown: function (e) { return e.preventDefault(); }, onClick: function () { return editor.chain().focus().toggleBold().run(); } }), _jsx(IconButton.L, { className: editor.isActive('italic') ? styles['active-action'] : '', Icon: _jsx(ItalicIcon, {}), onMouseDown: function (e) { return e.preventDefault(); }, onClick: function () { return editor.chain().focus().toggleItalic().run(); } }), _jsx(IconButton.L, { className: editor.isActive('underline') ? styles['active-action'] : '', Icon: _jsx(UnderlineIcon, {}), onMouseDown: function (e) { return e.preventDefault(); }, onClick: function () { return editor.chain().focus().toggleUnderline().run(); } })] }), _jsxs(ActionBar.RowSection, { children: [_jsx(IconButton.L, { className: editor.isActive({ textAlign: 'left' }) ? styles['active-action'] : '', Icon: _jsx(AlignLeftIcon, {}), onMouseDown: function (e) { return e.preventDefault(); }, onClick: function () { return editor.chain().focus().setTextAlign('left').run(); } }), _jsx(IconButton.L, { className: editor.isActive({ textAlign: 'center' }) ? styles['active-action'] : '', Icon: _jsx(AlignCenterIcon, {}), onMouseDown: function (e) { return e.preventDefault(); }, onClick: function () { return editor.chain().focus().setTextAlign('center').run(); } })] }), canApplyLink && (_jsx(ActionBar.RowSection, { children: _jsx(IconButton.L, { className: editor.isActive('link') ? styles['active-action'] : '', Icon: _jsx(LinkIcon, {}), onMouseDown: function (e) { return e.preventDefault(); }, onClick: openLinkPopup }) }))] }) }), _jsx(LinkPopup, { open: linkPopupOpen, onClose: function () { return setLinkPopupOpen(false); }, onSubmit: applyLink, onDelete: removeLink, initialState: { text: (_b = linkSelectionRef.current) === null || _b === void 0 ? void 0 : _b.text, url: (_c = linkSelectionRef.current) === null || _c === void 0 ? void 0 : _c.url } })] }));
};
